export type OpenSearchPathOption = {
  path: string;
  sampleValue: string;
  valueType: string;
};

function valueTypeOf(value: unknown): string {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  return typeof value;
}

function sampleString(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.length > 48 ? `${value.slice(0, 48)}…` : value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return `[${value.length}]`;
  if (typeof value === "object") return "{…}";
  return String(value);
}

type FlattenOptions = {
  maxDepth?: number;
  maxPaths?: number;
};

export function flattenOpenSearchPaths(
  root: unknown,
  options: FlattenOptions = {}
): OpenSearchPathOption[] {
  const maxDepth = options.maxDepth ?? 8;
  const maxPaths = options.maxPaths ?? 200;
  const results: OpenSearchPathOption[] = [];
  const seen = new Set<string>();

  const visit = (value: unknown, path: string, depth: number) => {
    if (results.length >= maxPaths) return;
    if (depth > maxDepth) return;

    const valueType = valueTypeOf(value);
    if (path && !seen.has(path)) {
      seen.add(path);
      results.push({
        path,
        sampleValue: sampleString(value),
        valueType,
      });
    }

    if (value === null || value === undefined) return;

    if (Array.isArray(value)) {
      const limit = Math.min(value.length, 5);
      for (let i = 0; i < limit; i += 1) {
        const childPath = path ? `${path}.${i}` : String(i);
        visit(value[i], childPath, depth + 1);
      }
      return;
    }

    if (typeof value === "object") {
      for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
        const childPath = path ? `${path}.${key}` : key;
        visit(child, childPath, depth + 1);
      }
    }
  };

  visit(root, "", 0);
  return results.sort((a, b) => a.path.localeCompare(b.path));
}
